import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Chatbot.css';
import {
  FaComments, FaTimes, FaPaperPlane, FaUser, FaCircle, FaRobot,
  FaMicrophone, FaVolumeUp, FaVolumeMute, FaRegFileAlt, FaRegCalendarCheck
} from 'react-icons/fa';
import { CAL_LINK, CAL_NAMESPACE, CAL_CONFIG, initCalPopup } from '../../config/cal';

const WEBHOOK_URL = 'https://myn8n.syedmaazsaeed.site/webhook/fc7ba60e-6bad-443c-8c9a-cc9acae09987';

const QUICK_PROMPTS = [
  'Is he available for hire?',
  'Summarize his experience in 3 lines',
  "What's his strongest AI project?",
  'Which project fits a Next.js role?',
];

const GREETING = {
  id: 1,
  text: "Hi! I'm Syed Maaz Saeed's AI assistant. Ask about his work or availability, tap the mic to talk, or paste a job description and I'll check his fit.",
  sender: 'bot',
  timestamp: new Date(),
};

// n8n can return a response in several shapes; normalise them all to a string.
const extractBotResponse = (data) => {
  if (data == null) return '';
  if (typeof data === 'string') return data;
  if (data.output) return data.output;
  if (data.message) return data.message;
  if (data.text) return data.text;
  if (Array.isArray(data) && data.length > 0) {
    const first = data[0];
    if (typeof first === 'string') return first;
    return first.output || first.message || first.text || JSON.stringify(first);
  }
  return JSON.stringify(data);
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [sttSupported, setSttSupported] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [showJd, setShowJd] = useState(false);
  const [jdText, setJdText] = useState('');
  const [isUserTyping, setIsUserTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const sendRef = useRef(null);
  const sessionIdRef = useRef('');
  const typingTimerRef = useRef(null);

  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Stable session id so n8n keeps conversation memory across messages.
  useEffect(() => {
    let sid = '';
    try {
      sid = localStorage.getItem('chatbotSessionId') || '';
      if (!sid) {
        sid = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem('chatbotSessionId', sid);
      }
      setVoiceEnabled(localStorage.getItem('chatbotVoice') === 'on');
    } catch (e) {
      sid = `session-${Date.now()}`;
    }
    sessionIdRef.current = sid;
    if ('speechSynthesis' in window) {
      // Warm up the voice list so the first spoken reply isn't silent.
      window.speechSynthesis.getVoices();
    }
  }, []);

  // Auto-scroll to the latest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus the input when the chat opens.
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Clear the typing-status timer on unmount.
  useEffect(() => () => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
  }, []);

  // Initialise the Cal.com booking popup so the in-chat "Book a call" button works.
  useEffect(() => {
    initCalPopup();
  }, []);

  const speak = useCallback((text) => {
    if (!voiceEnabled || !text || !('speechSynthesis' in window)) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'en-US';
      utter.rate = 1;
      utter.pitch = 1;
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(
        (v) => v.lang && v.lang.startsWith('en') &&
          /google|natural|samantha|microsoft|jenny|aria/i.test(v.name)
      );
      if (preferred) utter.voice = preferred;
      window.speechSynthesis.speak(utter);
    } catch (e) {
      /* ignore speech errors */
    }
  }, [voiceEnabled]);

  // Core send. `displayText` lets us show a short bubble while sending a longer prompt (JD matcher).
  const sendMessage = useCallback(async (rawText, displayText) => {
    const sent = (rawText ?? inputMessage).trim();
    if (!sent || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: displayText || sent,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsUserTyping(false);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    setIsLoading(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: sent, sessionId: sessionIdRef.current }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const botResponse = extractBotResponse(data) ||
        "I'm sorry, I didn't receive a valid response. Please try again.";
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, text: botResponse, sender: 'bot', timestamp: new Date() },
      ]);
      speak(botResponse);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "I'm sorry, I encountered an error. Please try again later.",
          sender: 'bot',
          timestamp: new Date(),
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, isLoading, speak]);

  // Keep the latest sendMessage reachable from the (mount-only) speech recognition handler.
  useEffect(() => {
    sendRef.current = sendMessage;
  }, [sendMessage]);

  // Set up speech recognition once.
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return undefined;
    setSttSupported(true);

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript;
      setIsListening(false);
      if (transcript) sendRef.current?.(transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;

    return () => {
      try { recognition.abort(); } catch (e) { /* ignore */ }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  // Track the user's own typing so the header shows a live "typing…" status.
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputMessage(value);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (!value.trim()) {
      setIsUserTyping(false);
      return;
    }
    setIsUserTyping(true);
    typingTimerRef.current = setTimeout(() => setIsUserTyping(false), 1200);
  };

  const toggleListening = () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    if (isListening) {
      try { recognition.stop(); } catch (e) { /* ignore */ }
      setIsListening(false);
      return;
    }
    try {
      window.speechSynthesis?.cancel();
      recognition.start();
      setIsListening(true);
    } catch (e) {
      setIsListening(false);
    }
  };

  const toggleVoice = () => {
    setVoiceEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem('chatbotVoice', next ? 'on' : 'off'); } catch (e) { /* ignore */ }
      if (!next && 'speechSynthesis' in window) window.speechSynthesis.cancel();
      return next;
    });
  };

  const toggleChat = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        if ('speechSynthesis' in window) window.speechSynthesis.cancel();
        try { recognitionRef.current?.abort(); } catch (e) { /* ignore */ }
        setIsListening(false);
      }
      return next;
    });
  };

  const submitJd = () => {
    const jd = jdText.trim();
    if (!jd || isLoading) return;
    const prompt =
      'JOB DESCRIPTION MATCH REQUEST:\n\n' +
      'Analyze how well Syed Maaz Saeed fits the role below. Reply with: ' +
      '(1) a match score out of 100, (2) the strengths that align, ' +
      '(3) any gaps, and (4) a one-line verdict.\n\n' +
      '--- JOB DESCRIPTION ---\n' + jd;
    setShowJd(false);
    setJdText('');
    sendMessage(prompt, '📋 I shared a job description, checking my fit…');
  };

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* Chat Button */}
      <button
        className={`chatbot__toggle ${isOpen ? 'chatbot__toggle--open' : ''}`}
        onClick={toggleChat}
        aria-label="Toggle chatbot"
      >
        {isOpen ? <FaTimes /> : <FaComments />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot__window">
          {/* Header */}
          <div className="chatbot__header">
            <div className="chatbot__header-content">
              <div className="chatbot__header-info">
                <div className="chatbot__header-title-wrapper">
                  <div className="chatbot__header-icon-wrapper">
                    <FaRobot className="chatbot__header-icon" />
                  </div>
                  <div>
                    <h3>AI Assistant</h3>
                    <div className="chatbot__status">
                      <span className="chatbot__status-dot"></span>
                      <span className="chatbot__status-text">
                        {isLoading ? 'Typing…' : (isUserTyping ? 'typing…' : 'Online')}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="chatbot__header-subtitle">Ask about work, projects, or availability</p>
              </div>
            </div>

            <div className="chatbot__header-actions">
              {ttsSupported && (
                <button
                  className={`chatbot__icon-btn ${voiceEnabled ? 'chatbot__icon-btn--active' : ''}`}
                  onClick={toggleVoice}
                  aria-label={voiceEnabled ? 'Turn voice replies off' : 'Turn voice replies on'}
                  title={voiceEnabled ? 'Voice replies: on' : 'Voice replies: off'}
                >
                  {voiceEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
                </button>
              )}
              <button
                className="chatbot__close-btn"
                onClick={toggleChat}
                aria-label="Close chatbot"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="chatbot__messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot__message chatbot__message--${message.sender} ${message.isError ? 'chatbot__message--error' : ''}`}
              >
                <div className="chatbot__message-avatar">
                  {message.sender === 'user' ? <FaUser /> : <FaRobot />}
                </div>
                <div className="chatbot__message-content">
                  <p className="chatbot__message-text">{message.text}</p>
                  <div className="chatbot__message-footer">
                    <span className="chatbot__message-time">{formatTime(message.timestamp)}</span>
                    {message.sender === 'user' && (
                      <span className="chatbot__message-status"><FaCircle /></span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="chatbot__message chatbot__message--bot">
                <div className="chatbot__message-avatar"><FaRobot /></div>
                <div className="chatbot__message-content">
                  <div className="chatbot__typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Recruiter quick-prompts (only on a fresh conversation) */}
          {messages.length === 1 && !showJd && !isLoading && (
            <div className="chatbot__quick">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q}
                  className="chatbot__quick-chip"
                  onClick={() => sendMessage(q)}
                  disabled={isLoading}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* JD matcher panel */}
          {showJd && (
            <div className="chatbot__jd">
              <textarea
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste a job description here and I'll score how well Maaz fits the role..."
              />
              <div className="chatbot__jd-actions">
                <button
                  className="chatbot__jd-cancel"
                  onClick={() => { setShowJd(false); setJdText(''); }}
                >
                  Cancel
                </button>
                <button
                  className="chatbot__jd-submit"
                  onClick={submitJd}
                  disabled={!jdText.trim() || isLoading}
                >
                  Analyze fit
                </button>
              </div>
            </div>
          )}

          {/* Tools bar */}
          <div className="chatbot__tools">
            <button
              className="chatbot__tool-btn"
              onClick={() => setShowJd((s) => !s)}
              disabled={isLoading}
            >
              <FaRegFileAlt /> {showJd ? 'Close job matcher' : 'Match a job description'}
            </button>
            <button
              type="button"
              className="chatbot__tool-btn chatbot__tool-btn--book"
              data-cal-namespace={CAL_NAMESPACE}
              data-cal-link={CAL_LINK}
              data-cal-config={CAL_CONFIG}
            >
              <FaRegCalendarCheck /> Book a call
            </button>
          </div>

          {/* Input */}
          <form className="chatbot__input-form" onSubmit={handleSubmit}>
            <div className="chatbot__input-wrapper">
              {sttSupported && (
                <button
                  type="button"
                  className={`chatbot__mic ${isListening ? 'chatbot__mic--active' : ''}`}
                  onClick={toggleListening}
                  aria-label={isListening ? 'Stop listening' : 'Speak'}
                  title={isListening ? 'Listening… click to stop' : 'Speak to the assistant'}
                  disabled={isLoading}
                >
                  <FaMicrophone />
                </button>
              )}
              <input
                ref={inputRef}
                type="text"
                className="chatbot__input"
                placeholder={isListening ? 'Listening…' : 'Type your message...'}
                value={inputMessage}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              <button
                type="submit"
                className="chatbot__send-btn"
                disabled={!inputMessage.trim() || isLoading}
                aria-label="Send message"
              >
                <FaPaperPlane />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
